import { useEffect } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';
import { connectivityMonitor } from '@/services/offline/connectivityMonitor';
import { useOfflineSessionStore } from '@/services/offline/offlineSessionStore';

const REASON_COPY = {
  'no-snapshot': {
    title: 'No saved session on this device',
    detail: 'This device has not stored an offline session yet. Connect to the internet once to sign in; offline access will be available afterwards.',
  },
  'token-expired': {
    title: 'Offline session expired',
    detail: 'For security, offline access ends 24 hours after your last connection. Reconnect to continue.',
  },
  'snapshot-mismatch': {
    title: 'Saved session is out of date',
    detail: 'The saved offline session does not match your current sign-in. Reconnect to refresh it.',
  },
  'solutions-unavailable': {
    title: 'Could not load your workspace',
    detail: 'Part of your saved session is missing. Reconnect to restore offline access.',
  },
};

export default function OfflineReconnectWall() {
  const bootReason = useOfflineSessionStore((s) => s.bootReason);
  const copy = REASON_COPY[bootReason] || REASON_COPY['no-snapshot'];

  // The moment the backend is reachable again, a plain reload takes the
  // normal online boot path (nothing is loaded yet, so nothing is lost).
  useEffect(() => {
    // Jittered: after an outage every waiting client sees the backend return
    // within seconds of each other.
    const unsubscribe = connectivityMonitor.subscribe((online) => {
      if (online) setTimeout(() => window.location.reload(), Math.random() * 20000);
    });
    connectivityMonitor.probeNow();
    return unsubscribe;
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
        <div className="mx-auto w-12 h-12 rounded-full bg-yellow-50 flex items-center justify-center mb-4">
          <WifiOff className="w-6 h-6 text-yellow-600" />
        </div>
        <h1 className="text-lg font-semibold text-gray-900">{copy.title}</h1>
        <p className="mt-2 text-sm text-gray-600">{copy.detail}</p>
        <p className="mt-1 text-xs text-gray-400">
          This page retries automatically when a connection is available.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-6 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-primary text-white hover:opacity-90"
        >
          <RefreshCw className="w-4 h-4" />
          Retry now
        </button>
      </div>
    </div>
  );
}
